import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import ImprimirParteModal from '../components/ImprimirParteModal';
import { useAuth } from '../context/AuthContext';
import { parteEntradaService, trabajadorService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/parte_entrada_list.css';
import '../styles/articulos.css';

const fmtFecha = (d) => (d ? d.split('T')[0] : '—');
const today    = () => new Date().toISOString().split('T')[0];
const weekAgo  = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; };

const ESTADO_CFG = {
  'GENERADO':       { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'APLICADO PAR.':  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'APLICADO TOTAL': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
};
const ESTADOS = ['GENERADO', 'APLICADO PAR.', 'APLICADO TOTAL'];

const PAGE_SIZES = [10, 25, 50, 100];

const EMPTY_FILTERS = {
  sucursal:       '',
  almacen:        '',
  proveedor:      '',
  tipoMovimiento: '',
  docRef1:        '',
  numRef1:        '',
  estado:         '',
  fechaInicio:    weekAgo(),
  fechaFin:       today(),
  sinFecha:       false,
};

export default function ParteEntradaList() {
  const navigate = useNavigate();
  const { sucursal: sesionSucursal } = useAuth();

  const [partes,   setPartes]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  // Por defecto filtra por la sucursal del usuario logueado
  const [filters,  setFilters]  = useState({ ...EMPTY_FILTERS, sucursal: sesionSucursal?.nombre || '' });
  const [pageSize, setPageSize] = useState(25);
  const [page,     setPage]     = useState(1);
  const [toast,    setToast]    = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [imprimirTarget, setImprimirTarget] = useState(null);
  const [trabajadores,   setTrabajadores]   = useState([]);
  const [verTarget,       setVerTarget]       = useState(null); // { id_parte_entrada }
  const [verData,         setVerData]         = useState(null); // { cabecera, detalle, trazabilidad }
  const [verLoading,      setVerLoading]      = useState(false);
  const [editarTarget,    setEditarTarget]    = useState(null);
  const [editarForm,      setEditarForm]      = useState(null);
  const [editarLoading,   setEditarLoading]   = useState(false);
  const [editarSaving,    setEditarSaving]    = useState(false);

  const setF = (key, val) => { setFilters(prev => ({ ...prev, [key]: val })); setPage(1); };

  const fetchPartes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await parteEntradaService.getAll();
      setPartes(r.data || []);
      setPage(1);
    } catch {
      setToast({ type: 'error', message: 'Error al cargar los partes de entrada.' });
    } finally { setLoading(false); }
  }, []);

  // Cargar trabajadores una vez (necesarios para el modal de impresión — firma de quien recepciona)
  useEffect(() => {
    trabajadorService.getAll()
      .then(r => setTrabajadores(r.data || []))
      .catch(() => { /* silencioso: si falla el modal solo mostrará lista vacía */ });
  }, []);

  useEffect(() => { fetchPartes(); }, [fetchPartes]);

  /* ── Opciones para autocompletes (extraídas del listado cargado) ── */
  const opsSucursal = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.sucursal_nombre && !seen.has(p.sucursal_nombre) && seen.add(p.sucursal_nombre))
      .map(p => ({ id: p.sucursal_nombre, nombre: p.sucursal_nombre }));
  }, [partes]);

  const opsAlmacen = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.almacen_nombre && !seen.has(p.almacen_nombre) && seen.add(p.almacen_nombre))
      .map(p => ({ id: p.almacen_nombre, nombre: p.almacen_nombre }));
  }, [partes]);

  const opsProveedor = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.proveedor_nombre && !seen.has(p.proveedor_nombre) && seen.add(p.proveedor_nombre))
      .map(p => ({ id: p.proveedor_nombre, nombre: p.proveedor_nombre, ruc: p.ruc_proveedor || '' }));
  }, [partes]);

  const opsDocRef1 = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.tipo_doc_ref1_descripcion && !seen.has(p.tipo_doc_ref1_descripcion) && seen.add(p.tipo_doc_ref1_descripcion))
      .map(p => ({ id: p.tipo_doc_ref1_descripcion, nombre: p.tipo_doc_ref1_descripcion }));
  }, [partes]);

  const opsTipoMov = useMemo(() => {
    const seen = new Set();
    return partes
      .filter(p => p.tipo_movimiento_descripcion && !seen.has(p.tipo_movimiento_descripcion) && seen.add(p.tipo_movimiento_descripcion))
      .map(p => ({ id: p.tipo_movimiento_descripcion, nombre: p.tipo_movimiento_descripcion }));
  }, [partes]);

  /* ── Filtrado ── */
  const filtered = useMemo(() => {
    return partes.filter(p => {
      if (filters.sucursal       && p.sucursal_nombre              !== filters.sucursal)        return false;
      if (filters.almacen        && p.almacen_nombre               !== filters.almacen)         return false;
      if (filters.proveedor      && p.proveedor_nombre             !== filters.proveedor)       return false;
      if (filters.tipoMovimiento && p.tipo_movimiento_descripcion  !== filters.tipoMovimiento)  return false;
      if (filters.estado         && p.estado                       !== filters.estado)          return false;
      if (filters.docRef1 && p.tipo_doc_ref1_descripcion !== filters.docRef1) return false;
      if (filters.numRef1 && !(p.num_doc_ref1 || '').toLowerCase().includes(filters.numRef1.toLowerCase())) return false;
      if (!filters.sinFecha) {
        const fecha = (p.fecha_operacion || '').slice(0, 10);
        if (filters.fechaInicio && fecha < filters.fechaInicio) return false;
        if (filters.fechaFin    && fecha > filters.fechaFin)    return false;
      }
      return true;
    });
  }, [partes, filters]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / (pageSize || filtered.length)));
  const currentPage = Math.min(page, totalPages);
  const paginated   = pageSize === 0
    ? filtered
    : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const limpiarFiltros = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  /* ── Ver PE ───────────────────────────────────────────────── */
  const abrirVer = async (pe) => {
    setVerTarget(pe);
    setVerData(null);
    setVerLoading(true);
    try {
      const [detRes, trazRes] = await Promise.all([
        parteEntradaService.getById(pe.id_parte_entrada),
        parteEntradaService.getTrazabilidad(pe.id_parte_entrada),
      ]);
      setVerData({ cabecera: detRes.data, trazabilidad: trazRes.data });
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'No se pudo cargar el parte.') });
      setVerTarget(null);
    } finally { setVerLoading(false); }
  };
  const cerrarVer = () => { setVerTarget(null); setVerData(null); };

  /* ── Editar PE ────────────────────────────────────────────── */
  const abrirEditar = async (pe) => {
    setEditarTarget(pe);
    setEditarForm(null);
    setEditarLoading(true);
    try {
      const res = await parteEntradaService.getById(pe.id_parte_entrada);
      const d = res.data || {};
      setEditarForm({
        derivado: !!d.derivado,
        fecha_operacion:    (d.fecha_operacion || '').slice(0, 10),
        id_almacen:         d.id_almacen || '',
        id_sucursal:        d.id_sucursal || '',
        id_tipo_movimiento: d.id_tipo_movimiento || '',
        ruc_proveedor:      d.ruc_proveedor || '',
        id_proveedor:       d.id_proveedor || '',
        num_doc_ref1:       d.num_doc_ref1 || '',
        num_doc_ref2:       d.num_doc_ref2 || '',
        observacion:        d.observacion || '',
        id_centro_costo:    d.id_centro_costo || '',
        id_solicitante:     d.id_solicitante || '',
        _numero:            d.numero,
        _almacen_nombre:    d.almacen_nombre,
        _proveedor_nombre:  d.proveedor_nombre,
        _tipo_movimiento:   d.tipo_movimiento_descripcion,
      });
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'No se pudo cargar el parte.') });
      setEditarTarget(null);
    } finally { setEditarLoading(false); }
  };
  const cerrarEditar = () => { setEditarTarget(null); setEditarForm(null); };

  const guardarEdicion = async () => {
    if (!editarTarget || !editarForm) return;
    if (!editarForm.fecha_operacion) {
      setToast({ type: 'error', message: 'La fecha es obligatoria.' }); return;
    }
    setEditarSaving(true);
    try {
      // Si el PE es derivado, solo mandamos fecha_operacion; el backend también lo impone.
      const payload = editarForm.derivado
        ? { fecha_operacion: editarForm.fecha_operacion }
        : {
            fecha_operacion: editarForm.fecha_operacion,
            observacion:     editarForm.observacion,
            num_doc_ref1:    editarForm.num_doc_ref1 || null,
            num_doc_ref2:    editarForm.num_doc_ref2 || null,
          };
      await parteEntradaService.update(editarTarget.id_parte_entrada, payload);
      setToast({ type: 'success', message: 'Parte de entrada actualizado.' });
      cerrarEditar();
      fetchPartes();
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    } finally { setEditarSaving(false); }
  };

  const handleEliminar = (pe) => {
    setConfirm({
      message: `¿Eliminar el Parte de Entrada N° ${pe.numero}?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await parteEntradaService.delete(pe.id_parte_entrada);
          setToast({ type: 'success', message: `Parte N° ${pe.numero} eliminado.` });
          setPartes(prev => prev.filter(p => p.id_parte_entrada !== pe.id_parte_entrada));
        } catch (e) {
          setToast({ type: 'error', message: getErrorDetail(e) });
        }
      },
    });
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) => {
    if (k === 'sinFecha')    return v === true;
    if (k === 'fechaInicio') return v !== weekAgo();
    if (k === 'fechaFin')    return v !== today();
    return !!v;
  }).length;

  return (
    <Layout title="Partes de Entrada" subtitle="Almacén">
      {toast   && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {confirm && (
        <Confirm
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {imprimirTarget && (
        <ImprimirParteModal
          tipo="entrada"
          parte={imprimirTarget}
          trabajadores={trabajadores}
          onClose={() => setImprimirTarget(null)}
        />
      )}

      {/* ── Encabezado ── */}
      <div className="pel-header">
        <h2 className="pel-title">Listado de Partes de Entrada</h2>
        <div className="pel-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchPartes}>↺ Actualizar</button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/almacen/parte-entrada')}>
            + Nuevo Parte
          </button>
        </div>
      </div>

      {/* ── Panel de filtros ── */}
      <div className="pel-filters">
        <div className="pel-filters-grid">

          {/* Sucursal */}
          <div className="pel-filter-field">
            <label>Sucursal</label>
            <AutoComplete
              value={filters.sucursal}
              options={opsSucursal}
              idField="id"
              displayField="nombre"
              selectedField="nombre"
              placeholder="Todas"
              onChange={val => setF('sucursal', val || '')}
            />
          </div>

          {/* Almacén */}
          <div className="pel-filter-field">
            <label>Almacén</label>
            <AutoComplete
              value={filters.almacen}
              options={opsAlmacen}
              idField="id"
              displayField="nombre"
              selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('almacen', val || '')}
            />
          </div>

          {/* Proveedor / Cliente */}
          <div className="pel-filter-field">
            <label>Proveedor / Cliente</label>
            <AutoComplete
              value={filters.proveedor}
              options={opsProveedor}
              idField="id"
              displayField={(o) => o.ruc ? `${o.ruc} — ${o.nombre}` : o.nombre}
              selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('proveedor', val || '')}
            />
          </div>

          {/* Tipo Movimiento */}
          <div className="pel-filter-field">
            <label>Tipo de Movimiento</label>
            <AutoComplete
              value={filters.tipoMovimiento}
              options={opsTipoMov}
              idField="id"
              displayField="nombre"
              selectedField="nombre"
              placeholder="Todos"
              onChange={val => setF('tipoMovimiento', val || '')}
            />
          </div>

          {/* Fecha desde */}
          <div className="pel-filter-field">
            <label>Fecha desde</label>
            <input
              type="date"
              className="pel-date-input"
              value={filters.fechaInicio}
              disabled={filters.sinFecha}
              onChange={e => setF('fechaInicio', e.target.value)}
            />
          </div>

          {/* Fecha hasta + Ver todo */}
          <div className="pel-filter-field">
            <label>Fecha hasta</label>
            <div className="pel-fecha-row">
              <input
                type="date"
                className="pel-date-input"
                value={filters.fechaFin}
                disabled={filters.sinFecha}
                onChange={e => setF('fechaFin', e.target.value)}
              />
              <button
                className={`pel-btn-todo ${filters.sinFecha ? 'active' : ''}`}
                title={filters.sinFecha ? 'Aplicar rango de fechas' : 'Ver todos sin filtro de fecha'}
                onClick={() => setF('sinFecha', !filters.sinFecha)}
              >
                {filters.sinFecha ? 'Con fecha' : 'Ver todo'}
              </button>
            </div>
          </div>

          {/* Doc Referencia 1 */}
          <div className="pel-filter-field">
            <label>Doc. Referencia 1</label>
            <AutoComplete
              value={filters.docRef1}
              options={opsDocRef1}
              idField="id"
              displayField="nombre"
              selectedField="nombre"
              placeholder="Tipo de documento..."
              onChange={val => setF('docRef1', val || '')}
            />
          </div>

          {/* Número Referencia 1 */}
          <div className="pel-filter-field">
            <label>N° Referencia 1</label>
            <input
              type="text"
              className="pel-text-input"
              placeholder="Número..."
              value={filters.numRef1}
              onChange={e => setF('numRef1', e.target.value)}
            />
          </div>

          {/* Estado */}
          <div className="pel-filter-field">
            <label>Estado</label>
            <select
              className="pel-select"
              value={filters.estado}
              onChange={e => setF('estado', e.target.value)}
            >
              <option value="">Todos</option>
              {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {/* Limpiar */}
          {activeFilters > 0 && (
            <div className="pel-filter-field pel-filter-clear">
              <label>&nbsp;</label>
              <button className="btn btn-secondary btn-sm" onClick={limpiarFiltros}>
                Limpiar filtros ({activeFilters})
              </button>
            </div>
          )}

        </div>
      </div>

      {/* ── Tabla ── */}
      <div className="pel-table-wrap">
        {loading ? (
          <div className="pel-loading">Cargando partes de entrada...</div>
        ) : filtered.length === 0 ? (
          <div className="pel-empty">No hay partes de entrada que coincidan con los filtros.</div>
        ) : (
          <table className="pel-table">
            <thead>
              <tr>
                <th>Sucursal</th>
                <th>Almacén</th>
                <th>Proveedor / Cliente</th>
                <th className="center">Fecha Op.</th>
                <th>Tipo Movimiento</th>
                <th>Doc. Referencia</th>
                <th className="center">Estado</th>
                <th className="center" style={{ width: 120 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(pe => {
                const est = ESTADO_CFG[pe.estado] || {};
                const docRef = pe.tipo_doc_ref1_descripcion
                  ? `${pe.tipo_doc_ref1_descripcion}${pe.num_doc_ref1 ? ' · ' + pe.num_doc_ref1 : ''}`
                  : pe.num_doc_ref1 || '—';
                return (
                  <tr key={pe.id_parte_entrada}>
                    <td>{pe.sucursal_nombre || '—'}</td>
                    <td>{pe.almacen_nombre || '—'}</td>
                    <td>
                      {pe.ruc_proveedor && <div className="pel-ruc">{pe.ruc_proveedor}</div>}
                      <div className="pel-razon">{pe.proveedor_nombre || '—'}</div>
                    </td>
                    <td className="center">{fmtFecha(pe.fecha_operacion)}</td>
                    <td>{pe.tipo_movimiento_descripcion || '—'}</td>
                    <td className="pel-docref">{docRef}</td>
                    <td className="center">
                      <span className="pel-badge" style={{
                        color: est.color, background: est.bg, border: `1px solid ${est.border || 'transparent'}`
                      }}>
                        {pe.estado || '—'}
                      </span>
                    </td>
                    <td className="center">
                      <div className="pel-actions">
                        <button className="pel-btn-action" title="Ver detalle"
                          onClick={() => abrirVer(pe)}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        <button className="pel-btn-action"
                          title={pe.bloqueado ? 'No editable: tiene PS o GR derivados' : 'Editar'}
                          onClick={() => abrirEditar(pe)}
                          disabled={!!pe.bloqueado}
                          style={pe.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="pel-btn-action pel-btn-danger"
                          title={pe.bloqueado ? 'No eliminable: tiene PS o GR derivados' : 'Eliminar'}
                          onClick={() => handleEliminar(pe)}
                          disabled={!!pe.bloqueado}
                          style={pe.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                        <button className="pel-btn-action" title="Imprimir"
                          onClick={async () => {
                            try {
                              const r = await parteEntradaService.getById(pe.id_parte_entrada);
                              setImprimirTarget(r.data);
                            } catch {
                              setToast({ type: 'error', message: 'Error al cargar el parte.' });
                            }
                          }}>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginación ── */}
      {!loading && filtered.length > 0 && (
        <div className="pel-pagination">
          <div className="pel-page-info">
            <span>Filas por página:</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              <option value={0}>Todos</option>
            </select>
            <span style={{ marginLeft: 12 }}>
              {pageSize === 0 ? filtered.length : Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–{pageSize === 0 ? filtered.length : Math.min(currentPage * pageSize, filtered.length)} de {filtered.length}
            </span>
          </div>
          {pageSize !== 0 && totalPages > 1 && (
            <div className="pel-page-nav">
              <button disabled={currentPage === 1} onClick={() => setPage(1)}>«</button>
              <button disabled={currentPage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...'
                  ? <span key={`e${i}`} className="pel-page-ellipsis">…</span>
                  : <button key={p} className={currentPage === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                )
              }
              <button disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              <button disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          )}
        </div>
      )}

      {/* ── Modal Ver PE ── */}
      {verTarget && (
        <div className="modal-overlay">
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Parte de Entrada — PE-{verTarget.numero}</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {verData?.cabecera?.almacen_nombre || verTarget.almacen_nombre || '—'}
                </div>
              </div>
              <button className="modal-close" onClick={cerrarVer}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              {verLoading || !verData ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : (
                <VerPeContenido data={verData} />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={cerrarVer}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Editar PE ── */}
      {editarTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Editar Parte de Entrada — PE-{editarTarget.numero}</h2>
                {editarForm?.derivado && (
                  <div style={{ fontSize: 11.5, color: '#fb923c', marginTop: 3 }}>
                    ⚠ Este parte proviene de un documento relacionado; solo puede modificarse la fecha de ingreso.
                  </div>
                )}
              </div>
              <button className="modal-close" onClick={cerrarEditar}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              {editarLoading || !editarForm ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
              ) : (
                <EditarPeForm form={editarForm} onChange={setEditarForm} />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={cerrarEditar} disabled={editarSaving}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarEdicion} disabled={editarSaving || editarLoading}>
                {editarSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── Subcomponentes del modal Ver — pestañas horizontales ── */
const VerPeContenido = ({ data }) => {
  const c = data.cabecera || {};
  const t = data.trazabilidad || {};
  const fmtFchS = (f) => (f || '').slice(0, 10) || '—';
  const [tab, setTab] = React.useState('info');

  const countOrigen = (t.ocs_origen   || []).length + (t.grs_origen   || []).length;
  const countDeriv  = (t.ps_derivados || []).length + (t.grs_derivados || []).length;

  const tabs = [
    { key: 'info',      label: 'Información',            color: '#60a5fa', count: null },
    { key: 'origen',    label: 'Comprobantes origen',    color: '#a78bfa', count: countOrigen },
    { key: 'derivados', label: 'Comprobantes derivados', color: '#22c55e', count: countDeriv },
  ];

  const Field = ({ label, value }) => (
    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Tabs horizontales */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto' }}>
        {tabs.map(tb => {
          const active = tab === tb.key;
          return (
            <button key={tb.key} type="button" onClick={() => setTab(tb.key)}
              style={{
                padding: '10px 18px', background: active ? 'rgba(15,27,45,0.8)' : 'transparent',
                border: 'none', borderBottom: active ? `2px solid ${tb.color}` : '2px solid transparent',
                color: active ? '#e2e8f0' : '#94a3b8', fontSize: 13, fontWeight: active ? 600 : 500,
                cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: tb.color }} />
              {tb.label}
              {tb.count !== null && (
                <span style={{ padding: '1px 7px', fontSize: 10, borderRadius: 10, background: tb.count > 0 ? tb.color : 'rgba(148,163,184,0.15)', color: tb.count > 0 ? '#0f1b2d' : '#64748b', fontWeight: 700 }}>{tb.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contenido de la pestaña */}
      {tab === 'info' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Cabecera */}
          <div style={{ padding: 14, borderRadius: 10, background: 'rgba(15,27,45,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <Field label="Número" value={`PE-${c.numero}`} />
              <Field label="Fecha de ingreso" value={fmtFchS(c.fecha_operacion)} />
              <Field label="Almacén" value={c.almacen_nombre} />
              <Field label="Sucursal" value={c.sucursal_nombre} />
              <Field label="Tipo Movimiento" value={c.tipo_movimiento_descripcion} />
              <Field label="Estado" value={c.estado} />
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
              <Field label="Proveedor" value={c.proveedor_nombre} />
              <Field label="RUC" value={c.ruc_proveedor} />
              <Field label="Solicitante" value={c.solicitante_nombre} />
              <Field label="Centro de Costo" value={c.centro_costo_descripcion} />
            </div>
            {(c.num_doc_ref1 || c.num_doc_ref2) && (
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
                {c.num_doc_ref1 && <Field label={c.tipo_doc_ref1_descripcion || 'Doc. Ref. 1'} value={c.num_doc_ref1} />}
                {c.num_doc_ref2 && <Field label={c.tipo_doc_ref2_descripcion || 'Doc. Ref. 2'} value={c.num_doc_ref2} />}
              </div>
            )}
            {c.observacion && (
              <div style={{ marginTop: 14 }}>
                <Field label="Observación" value={c.observacion} />
              </div>
            )}
          </div>

          {/* Ítems */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              Ítems ({(c.detalle || []).length})
            </div>
            <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
              <table className="data-table" style={{ width: '100%', margin: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>Código</th>
                    <th>Descripción</th>
                    <th style={{ width: 80 }}>Unidad</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Cantidad</th>
                    <th>Observación</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.detalle || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 18 }}>Sin ítems</td></tr>
                  ) : (c.detalle || []).map((d, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{d.articulo_codigo || '—'}</td>
                      <td>{d.articulo_descripcion || '—'}</td>
                      <td>{d.unidad_descripcion || d.unidad_codigo || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{(parseFloat(d.cantidad) || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 })}</td>
                      <td style={{ color: '#94a3b8' }}>{d.observacion || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'origen' && (
        countOrigen === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
            Este parte de entrada no proviene de otro documento.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <TrazabilidadBlock
              title="Orden de Compra origen" color="#60a5fa"
              items={t.ocs_origen || []}
              render={o => `${o.serie_codigo ? o.serie_codigo.trim() + '-' : 'OC-'}${o.numero} · ${fmtFchS(o.fecha_emision)}${o.proveedor_nombre ? ' · ' + o.proveedor_nombre : ''}`}
              estado={o => o.estado}
            />
            <TrazabilidadBlock
              title="Guía de Remisión origen" color="#a78bfa"
              items={t.grs_origen || []}
              render={g => `${g.serie_codigo ? g.serie_codigo.trim() + '-' : 'GR-'}${g.numero} · ${fmtFchS(g.fecha_guia)}${g.almacen_origen_nombre ? ' · Origen: ' + g.almacen_origen_nombre : ''}`}
              estado={g => g.estado}
            />
          </div>
        )
      )}

      {tab === 'derivados' && (
        countDeriv === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
            Aún no se han generado comprobantes derivados desde este parte de entrada.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <TrazabilidadBlock
              title="Partes de Salida derivados" color="#fb923c"
              items={t.ps_derivados || []}
              render={p => `PS-${p.numero} · ${fmtFchS(p.fecha_operacion)}${p.almacen_nombre ? ' · ' + p.almacen_nombre : ''}`}
              estado={p => p.estado}
            />
            <TrazabilidadBlock
              title="Guías de Remisión derivadas" color="#22c55e"
              items={t.grs_derivados || []}
              render={g => `${g.serie_codigo ? g.serie_codigo.trim() + '-' : 'GR-'}${g.numero} · ${fmtFchS(g.fecha_guia)}${g.almacen_destino_nombre ? ' → ' + g.almacen_destino_nombre : ''}`}
              estado={g => g.estado}
            />
          </div>
        )
      )}
    </div>
  );
};

const TrazabilidadBlock = ({ title, color, items, render, estado }) => (
  <div style={{
    padding: 12, borderRadius: 10,
    background: 'rgba(15,27,45,0.5)', border: `1px solid ${color}33`, borderLeft: `3px solid ${color}`,
  }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
      {title} ({items.length})
    </div>
    {items.length === 0 ? (
      <div style={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic' }}>Sin registros</div>
    ) : (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it, i) => (
          <li key={i} style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>{render(it)}</span>
            {estado(it) && (
              <span style={{ fontSize: 10, fontWeight: 700, color, padding: '1px 6px', background: `${color}22`, borderRadius: 4, border: `1px solid ${color}44` }}>
                {estado(it)}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/* ── Subcomponente del modal Editar ───────────────────────── */
const EditarPeForm = ({ form, onChange }) => {
  const set = (key, val) => onChange({ ...form, [key]: val });
  const labelS = { fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, marginBottom: 4, display: 'block' };
  const inpS   = {
    width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
    color: '#e2e8f0', outline: 'none', boxSizing: 'border-box',
  };
  const inpRO  = { ...inpS, background: 'rgba(255,255,255,0.02)', color: '#64748b' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelS}>Número</label>
          <input style={inpRO} value={`PE-${form._numero || ''}`} readOnly />
        </div>
        <div>
          <label style={labelS}>Fecha de ingreso *</label>
          <input type="date" style={inpS} value={form.fecha_operacion || ''} onChange={e => set('fecha_operacion', e.target.value)} />
        </div>
        <div>
          <label style={labelS}>Almacén</label>
          <input style={inpRO} value={form._almacen_nombre || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Tipo de Movimiento</label>
          <input style={inpRO} value={form._tipo_movimiento || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Proveedor</label>
          <input style={inpRO} value={form._proveedor_nombre || '—'} readOnly />
        </div>
        <div>
          <label style={labelS}>Doc. Ref. 1</label>
          <input style={form.derivado ? inpRO : inpS} value={form.num_doc_ref1 || ''} readOnly={!!form.derivado}
                 onChange={e => set('num_doc_ref1', e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={labelS}>Doc. Ref. 2</label>
          <input style={form.derivado ? inpRO : inpS} value={form.num_doc_ref2 || ''} readOnly={!!form.derivado}
                 onChange={e => set('num_doc_ref2', e.target.value)} placeholder="—" />
        </div>
      </div>
      <div>
        <label style={labelS}>Observación</label>
        <textarea
          rows={3}
          style={{ ...(form.derivado ? inpRO : inpS), resize: 'vertical' }}
          value={form.observacion || ''}
          readOnly={!!form.derivado}
          onChange={e => set('observacion', e.target.value)}
          placeholder={form.derivado ? 'No editable en parte derivado' : 'Observación del parte...'}
        />
      </div>
    </div>
  );
};
